import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { useQuery } from '@apollo/client';
import { useProfileContext } from './UserProfile.context';
import { PAGE_FILTER } from 'queries/PageFilter';
import { PAGE_FILTER_CNH_LIST_VIEW } from 'queries/PageFilterCnhListView';
import { PAGE_FILTER_EXPORT } from 'queries/PageFilterExport';
import { PAGE_FILTER_SALES_STOCK } from 'queries/PageFilterSalesStock';
import { getIsVehicleDetailsPage } from 'utils/environment';

export const CascadingFilters = createContext({});

const QUERY_BY_PATH = {
  '/other-distributors': PAGE_FILTER_EXPORT,
  '/sales-and-stock': PAGE_FILTER_SALES_STOCK,
  '/vehicle-list': PAGE_FILTER_CNH_LIST_VIEW,
};

const getQuery = ({ pathname }) => QUERY_BY_PATH[pathname] ?? PAGE_FILTER;

const getIsSkip = ({ pathname }) => getIsVehicleDetailsPage({ pathname });

const getInitialCascadingFilters = (selectedUserProfile) => {
  const initialFilters = get(selectedUserProfile, 'value.cascading') || [];

  // temporary for https://toyota.atlassian.net/browse/CUBE-47880
  // more permanent and recurring solution to be discussed

  // check if there are duplicate keys with bool values (true/false) and if so, remove them
  const grouped = initialFilters.reduce((acc, obj) => {
    acc[obj.field] = acc[obj.field] || [];
    acc[obj.field].push(obj);
    return acc;
  }, {});

  const result = Object.values(grouped)
    .flat()
    .filter((obj) => {
      const hasTrue = grouped[obj.field].some((item) => item.value === true && item.isChecked);
      const hasFalse = grouped[obj.field].some((item) => item.value === false && item.isChecked);
      return !(hasTrue && hasFalse);
    });

  return result;
};

const CascadingFiltersProvider = ({ children }) => {
  const { pathname } = useLocation();

  const { selectedUserProfile, setFilters } = useProfileContext();
  const { data } = useQuery(getQuery({ pathname }), { skip: getIsSkip({ pathname }) });
  const [cascadingFilters, setCascadingFilters] = useState(
    getInitialCascadingFilters(selectedUserProfile),
  );

  useEffect(() => {
    setCascadingFilters(getInitialCascadingFilters(selectedUserProfile));
  }, [selectedUserProfile]);

  const setCascadingFiltersHandler = (filters) => {
    setCascadingFilters(filters);

    // TODO: after removing filter management from profile, need to get back here and reevaluate if such approach is still needed
    setFilters({ filterType: 'cascading', filters });
  };

  const contextValue = useMemo(
    () => ({
      cascadingFilters,
      data,
      setCascadingFilters: setCascadingFiltersHandler,
    }),
    [cascadingFilters, data, setCascadingFiltersHandler],
  );

  return <CascadingFilters.Provider value={contextValue}>{children}</CascadingFilters.Provider>;
};

CascadingFiltersProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default CascadingFiltersProvider;

export const useCascadingFilterContext = () => useContext(CascadingFilters);
